import { rgba } from 'polished';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { AddIcon, CloseIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { Popover } from './Popover';
import { Select } from './Select';
import { Skeleton } from './Skeleton';
import { Label } from './Text';

const Container = styled(Flex)`
	border-radius: 5px;
	background: ${rgba(tokens.border.subtle, 0.25)};
	flex-wrap: wrap;
`;

const Item = styled(Flex)`
	border-radius: 2px;
	background: ${(props) => props.theme.colors.white};
	box-shadow:
		0px 0px 0px 1px ${rgba(tokens.text.secondary, 0.25)},
		0px 2px 4px ${rgba(tokens.text.secondary, 0.25)};
	height: 32px;
`;

const ItemTitle = styled(Flex)`
	padding: 4px 8px;
	align-items: center;
`;

const AddButton = styled(Box)<{ enabled?: boolean }>`
	background: ${tokens.border.subtle};
	width: 32px;
	height: 32px;
	padding: 4px;
	border-radius: 16px;

	svg {
		display: block;
		width: 24px;
		height: 24px;
	}

	${(props) =>
		props.enabled !== false &&
		`
		cursor: pointer;
		
		&:hover {
			background: ${props.theme.colors.lightBlueDark};

			&:active {
				background: ${tokens.text.secondary};

				svg {
					path {
						fill: ${props.theme.colors.white};
					}
				}
			}
		}`}
`;

const DeleteButton = styled(Flex)`
	border-left: 1px solid ${tokens.border.subtle};
	align-items: center;
	justify-content: center;
	flex-grow: 0;
	flex-shrink: 0;
	width: 32px;
	cursor: pointer;

	svg {
		display: block;
		width: 16px;
		height: 16px;
	}

	&:hover {
		background: ${rgba(tokens.border.subtle, 0.25)};

		&:active {
			background: ${rgba(tokens.border.subtle, 0.5)};
		}
	}
`;

function MultiSelectValue(props: { title: string; onDelete?: () => void }) {
	return (
		<Item ml={2} mb={2}>
			<ItemTitle>{props.title}</ItemTitle>
			{props.onDelete && (
				<DeleteButton onClick={() => props.onDelete?.()}>
					<CloseIcon />
				</DeleteButton>
			)}
		</Item>
	);
}

MultiSelectValue.Skeleton = function MultiSelectValueSkeleton() {
	return (
		<Item ml={2} mb={2}>
			<ItemTitle>
				<Skeleton.Box width={120} height={20} />
			</ItemTitle>
		</Item>
	);
};

type MultiSelectOption = {
	title: string;
	value: string;
};

type Props = {
	selectedOptions: MultiSelectOption[];
	onChange: (values: string[]) => void;
	required?: boolean;
	allowMultiSelect?: boolean;
	options?: MultiSelectOption[];
	fetch?: () => void;
};

export function MultiSelect({
	required,
	allowMultiSelect,
	selectedOptions,
	options,
	onChange,
	fetch,
}: Props) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (visible) {
			fetch?.();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible]);

	const remainingOptions = options?.filter(
		({ value }) => !selectedOptions.find((option) => value === option.value),
	);

	return (
		<Flex alignItems="center">
			<Container alignItems="center" p={2} pl={0} pb={0} flexGrow={1} flexShrink={1}>
				{selectedOptions.length > 2 && remainingOptions?.length === 0 ? (
					<MultiSelectValue
						title={t('select.multi.selection.all', { count: selectedOptions.length })}
						onDelete={() => onChange([])}
					/>
				) : (
					selectedOptions.map(({ title, value }) => (
						<MultiSelectValue
							key={value}
							title={title}
							onDelete={
								!required || selectedOptions.length > 1
									? () => {
											onChange(
												selectedOptions
													.filter((option) => option.value !== value)
													.map(({ value }) => value),
											);
										}
									: undefined
							}
						/>
					))
				)}
				{selectedOptions.length === 0 && (
					<Label ml={2} mb={2} style={{ lineHeight: '32px' }}>
						{t('common.multiSelect.empty')}
					</Label>
				)}
			</Container>
			<Box flexGrow={0} flexShrink={0} ml={2}>
				<Popover.Target
					visible={visible}
					onDismiss={() => setVisible(false)}
					contents={
						<Select.Menu
							options={((options?.length ?? 0) > 2
								? [
										{
											title: t('common.selectAll', { count: options?.length }),
											value: '*',
										},
										undefined,
									]
								: []
							).concat(remainingOptions)}
							onChange={(value?: string) => {
								if (!allowMultiSelect) {
									setVisible(false);
								}

								if (value === undefined) {
									return;
								}

								if (value === '*') {
									onChange(options?.map(({ value }) => value) ?? []);
								} else {
									onChange(selectedOptions.map(({ value }) => value).concat([value]));
								}
							}}
						/>
					}
				>
					{(!remainingOptions || remainingOptions.length > 0) && (
						<AddButton onClick={() => setVisible(true)}>
							<AddIcon />
						</AddButton>
					)}
				</Popover.Target>
			</Box>
		</Flex>
	);
}

MultiSelect.Skeleton = function MultiSelectSkeleton() {
	return (
		<Flex alignItems="center">
			<Container alignItems="center" p={2} pl={0} pb={0} flexGrow={1} flexShrink={1}>
				<Skeleton.Repeat count={3}>
					<MultiSelectValue.Skeleton />
				</Skeleton.Repeat>
			</Container>
			<Box flexGrow={0} flexShrink={0} ml={2}>
				<AddButton>
					<AddIcon />
				</AddButton>
			</Box>
		</Flex>
	);
};
